<template>

  <div class="live-bids p-2" @keyup="keyUp" tabindex="0">
    <div class="bg-white d-flex flex-column h-100 pt-2 pr-2 pl-2 pb-2">
      <spinner :show="loading"/>
      <div v-if="!loading" class="main h-100 d-flex flex-column">
        <div class="header-content border-bottom mb-3 d-flex justify-content-between" v-if="sale || lotNo">
          <div v-if="sale">
            <h2>
              <router-link v-permission="'view_auction'" :to="{ name: 'sales.view', params: { id: sale.uuid }}">
                <b>{{ sale.sale_no }}</b>
              </router-link>
              - {{ sale.title | masterLanguage }}
            </h2>
            <div>
              {{ sale.start_date | toDateTime }}
              <template v-if="sale.end_date">
                - {{ sale.end_date | toDateTime }}
              </template>
              - <b>{{ sale.department.name | masterLanguage }}</b>
            </div>
          </div>
          <div class="d-flex" v-if="lotNo">
            <b-button style="width: 15em" :disabled="!previousLotUuid" @click="getOtherLot(previousLotUuid)"
                      variant="outline-primary"
                      class="mb-0 mr-2 align-self-baseline">
              <font-awesome-icon icon="angle-double-left" class="icon mr-2"/>
              {{ $t('Previous Item') }}
            </b-button>
            <b-input type="text" v-model="lotNo"
                     style="width: 15em"
                     :class="!isLotNoInputFocused? 'text-center lot-no-input': 'lot-no-input'"
                     @keyup="onLotInputKeyUp" @blur="onLotInputBlur" @focus.native="onFocus"/>
            <b-button style="width: 15em" :disabled="!nextLotUuid" @click="getOtherLot(nextLotUuid)"
                      variant="outline-primary"
                      class="mb-0 ml-2 align-self-baseline">
              {{ $t('Next Item') }}
              <font-awesome-icon icon="angle-double-right" class="icon ml-2"/>
            </b-button>
          </div>
        </div>
        <div v-if='lotNo' class="content-wrapper d-flex pb-3">
          <div class="left-column d-flex flex-column pr-2 border-right">
            <div class="row auctioneer-console-content" v-if="lotNo">
              <div class="col-md-8 col-sm-12">
                <b-card>
                  <template v-slot:header>
                    <h5 class="mb-0 d-inline-block">{{ $t('Lot Information') }}</h5>
                  </template>
                  <div class="card-body pl-0 pr-0 mb-4">
                    <div class="row">
                      <div class="col-sm-5 border-right">
                        <table class="table table-sm mb-0" v-if="activeLot && sale">
                          <tr>
                            <td class="border-top-0 font-weight-bold">{{ $t('Stock') }}</td>
                            <td class="border-top-0 "> {{ activeLot.inventory.stock_no }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">{{ $t('Status') }}</td>
                            <td>
                            <span v-as-status="activeLot.status">
                              {{ activeLot.status | removeUnderscores | toUpperCaseFirst }}
                            </span>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">{{ $t('Hammer Price') }}</td>
                            <td> {{ activeLot.hammer_price | asCurrency(sale.currency.symbol) }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">{{ $t('Start Price') }}</td>
                            <td>
                              <span>{{ activeLot.start_price | asCurrency(sale.currency.symbol) }}</span>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">{{ $t('Reserve') }}</td>
                            <td>
                              <span>{{ activeLot.reserve | asCurrency(sale.currency.symbol) }}</span>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold ">{{ $t('Buyer') }}</td>
                            <td>
                              {{ activeLot.buyer ? activeLot.buyer.display_name : '' }}
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">{{ $t('Paddle Number') }}</td>
                            <td>
                              {{ activeLot.paddle_number }}
                            </td>
                          </tr>

                        </table>
                      </div>
                      <div class="col-sm-7">
                        <div style="max-height: 25em" class="overflow-auto">
                          <dynamic-fields-info-table :dynamic-fields="activeLot.inventory.dynamic_fields"
                                                     skip-empty-values
                                                     :dynamic-fields-template="sortedDynamicFieldsTemplate"></dynamic-fields-info-table>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-card>
                <b-card class="mb-3 mt-3">
                  <template v-slot:header>
                    <b>{{ $t('Commission and Telephone bids') }}</b>
                  </template>
                  <b-card-text class="p-0">
                    <table class="info-table table table-sm mb-0"
                           v-if="activeLot.pre_bids && activeLot.pre_bids.length > 0">
                      <tr>
                        <th class="border-top-0">{{ $t('Paddle') }}</th>
                        <th class="border-top-0">{{ $t('Customer') }}</th>
                        <th class="border-top-0">{{ $t('Bid') }}</th>
                        <th class="border-top-0">{{ $t('Type') }}</th>
                        <th class="border-top-0">{{ $t('Status') }}</th>
                      </tr>
                      <tr v-for="(bid, index) in sortedPreBids" :key="index">
                        <td>{{ bid.paddle_number }}</td>
                        <td>{{ bid.bidder.customer.display_name }}</td>
                        <td><span v-if="sale">{{ bid.amount | asCurrency(sale.currency.symbol) }}</span></td>
                        <td>
                          {{ bid.type | toUpperCaseFirst }}
                        </td>
                        <td>
                          <span v-as-status="bid.status">{{ bid.status }}</span>
                        </td>
                      </tr>
                    </table>
                    <div v-else>
                      <no-data-available :height="5"/>
                    </div>
                  </b-card-text>
                </b-card>
              </div>
              <div class="col-md-4 col-sm-12">
                <div class="img-container">
                  <div class="w-100 mb-4 h-100">
                    <b-carousel v-if="activeLot && activeLot.inventory.images.length"
                                v-model="slide" :interval="0" controls indicators>
                      <b-carousel-slide v-for="image in activeLot.inventory.images" :img-src="image.data.lg.url"
                                        :key="image.uuid"></b-carousel-slide>
                    </b-carousel>
                    <b-img v-else class="img-fluid" src='@/assets/no-image.jpg'/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if='lotNo' class="content-footer d-flex justify-content-between border-top pt-2 pb-0">
          <div>
            <b-button v-b-modal.helpModal variant="link" class="mx-2">
              <font-awesome-icon :icon="'info-circle'"/>
              {{ $t('Quick Shortcuts') }}
            </b-button>
          </div>
        </div>
        <div v-if="!lotNo">
          {{ $t('Following sale has no lots') }}
        </div>
      </div>
    </div>
    <shortcuts-modal :shortcuts="shortcuts"/>
  </div>


</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import Spinner from "@/core/components/common/Spinner";
  import ShortcutsModal from "@/modules/live-bids/ShortcutsModal";
  import DynamicFieldsInfoTable from "@/modules/inventory/view/dashboard/DynamicFieldsInfoTable";
  import helperService from "@/core/services/helper.service";
  import SalesService from "@/modules/sales/SalesService";

  const {mapActions, mapState} = createNamespacedHelpers('lots');

  export default {
    name: "AuctioneerConsole",
    components: {DynamicFieldsInfoTable, ShortcutsModal, Spinner, NoDataAvailable},
    data() {
      return {
        lotNo: null,
        nextLotUuid: null,
        previousLotUuid: null,
        expand: 'inventory.images,auction.department,auction.currency,pre_bids.bidder.customer,buyer,inventory.department',
        sortedPreBids: [],
        slide: 0,
        shortcuts: [
          {key: this.$t('right arrow'), action: this.$t('Move to the next lot')},
          {key: this.$t('left arrow'), action: this.$t('Move to the previous lot')}
        ],
        isLotNoInputFocused: false,
        saleNo: null,
      }
    },
    computed: {
      ...mapState({
        activeLot: state => state.auctioneerConsole.activeLot,
        loading: state => state.auctioneerConsole.loading,
        sale: state => state.auctioneerConsole.sale,
      }),
      sortedDynamicFieldsTemplate() {
        return helperService.calculateDynamicFieldsTemplateFromFields(this.activeLot.inventory.dynamic_fields, true)
      }
    },
    methods: {
      ...mapActions(['getLot', 'getLotPreviousAndNext', 'getSaleLot', 'activateLot', 'updateAuctioneerConsoleSale']),
      async getOtherLot(uuid) {
        if (!uuid) {
          return
        }
        let res = await this.getLot({uuid, expand: this.expand})
        if (res.success) {
          this.activateLot(res.body)
          this.lotNo = res.body.lot_no
        } else if (res.toastError) {
          this.$errorDialog(res.toastError);
        }
        if (this.$route.query.lot_no !== uuid) {
          await this.$router.push({
            name: 'auctioneerConsole',
            params: {saleNo: this.saleNo},
            query: {lot_no: uuid}
          })
        }
        await this.getPreviousAndNext()
        return this.activeLot
      },
      async getPreviousAndNext() {
        let res = await this.getLotPreviousAndNext(this.activeLot.uuid)
        if (res.success) {
          this.nextLotUuid = res.body.next
          this.previousLotUuid = res.body.previous
        } else if (res.toastError) {
          this.$errorDialog(res.toastError);
        }
      },
      async onLotInputKeyUp($event) {
        if (this.loading) {
          return
        }
        this.lotNo = $event.target.value.toUpperCase();
        if ($event.keyCode === 13) {
          $event.preventDefault();
          await this.getOtherLotWithLotNo(this.lotNo)
        }
      },
      async onLotInputBlur() {
        if (this.loading) {
          return
        }
        this.isLotNoInputFocused = false
        await this.getOtherLotWithLotNo(this.lotNo)
      },
      async getOtherLotWithLotNo(lotNo) {
        if (this.lotNo === this.activeLot.lot_no) {
          return
        }
        let tempLot = await this.getSaleLot({
          sale_no: this.sale.sale_no,
          expand: this.expand,
          lot_no: lotNo,
        });
        if (!tempLot) {
          this.lotNo = this.activeLot.lot_no
          this.$errorDialog(
            this.$t('Lot not found')
          );
          return
        }
        await this.$router.push({
          name: 'auctioneerConsole',
          params: {saleNo: this.saleNo},
          query: {lot_no: tempLot.uuid}
        })
        await this.getPreviousAndNext()
      },
      keyUp(evt) {
        //39 - right arrow | 37 - left array
        if (evt.which === 37) {
          this.getOtherLot(this.previousLotUuid)
        } else if (evt.which === 39) {
          this.getOtherLot(this.nextLotUuid)
        }
      },
      onFocus() {
        this.isLotNoInputFocused = true
      }
    },
    watch: {
      activeLot() {
        if (this.activeLot && this.activeLot.pre_bids) {
          this.sortedPreBids = this.activeLot.pre_bids.sort((a, b) => {
            if (a.amount - b.amount > 1) {
              return -1;
            } else if (a.amount - b.amount < 1) {
              return 1;
            } else {
              return 0;
            }
          });
        }
      },
    },
    async mounted() {
      this.saleNo = this.$route.params.saleNo;
      let activeLotUuid = this.$route.query.lot_no;

      let saleResponse = await SalesService.getSales({
        expand: 'department,currency',
        sale_no: this.$route.params.saleNo,
        limit: 1
      })

      if (!saleResponse.success || saleResponse.body.results.length === 0) {
        return
      }
      this.updateAuctioneerConsoleSale(saleResponse.body.results[0])

      if (this.$route.query.type === 'sale') {
        const response = await this.getSaleLot({
          sale_no: this.$route.params.saleNo,
          expand: this.expand,
          ordering: 'lot_no',
          limit: 1
        });
        if (response && response.uuid) {
          activeLotUuid = response.uuid
        }
      }
      const lot = await this.getOtherLot(activeLotUuid)
      if (this.activeLot && this.activeLot.uuid) {
        this.lotNo = lot && lot.lot_no
        await this.getPreviousAndNext()
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables.scss";

  .live-bids {
    @include list-view-wrapper;
  }

  .content-wrapper {
    flex: 1;
    overflow: hidden;

    .left-column {
      height: 100%;
      flex: 1;
      overflow-y: auto;
    }
  }


  @media only screen and (max-width: 1200px) {
    .content-wrapper {
      flex-direction: column;
      align-content: stretch;

      .left-column {
        padding-right: 0 !important;
        padding-bottom: 1em;
        margin-bottom: 1em;
        border-right: none !important;
        flex: 1;
      }

    }

    .header-content {
      display: block !important;
    }
  }

</style>
